import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

type Lang = 'fr' | 'en' | 'es';

interface SocialNotifyData {
  serverId: string;
}

/** Normalise une langue potentielle (fr, fr-FR, FR, etc.) vers 'fr' | 'en' | 'es' */
function normalizeLang(input?: string | null): Lang {
  const v = String(input ?? '').toLowerCase().trim();
  if (v.startsWith('en')) return 'en';
  if (v.startsWith('es')) return 'es';
  return 'fr';
}

/** Essaie de déduire la langue de l’URL (/en/..., /es/..., /fr/...) */
function getLangFromPath(pathname?: string | null): Lang | undefined {
  if (!pathname) return undefined;
  const seg = pathname.replace(/^\/+/, '').split('/')[0]?.toLowerCase();
  if (seg === 'en' || seg === 'es' || seg === 'fr') return seg as Lang;
  return undefined;
}

/** Résolution robuste de la langue côté client */
function resolveClientLanguage(ctxLanguage?: string | null): Lang {
  // 1) Prefixe d’URL
  const pathLang = typeof window !== 'undefined' ? getLangFromPath(window.location.pathname) : undefined;
  if (pathLang) return pathLang;

  // 2) <html lang="...">
  const htmlLang = typeof document !== 'undefined' ? document.documentElement.getAttribute('lang') : null;
  const htmlNorm = normalizeLang(htmlLang);
  if (htmlLang && htmlNorm) return htmlNorm;

  // 3) Contexte (LanguageContext)
  if (ctxLanguage) return normalizeLang(ctxLanguage);

  // 4) Navigator (dernier recours)
  const navLang = typeof navigator !== 'undefined' ? navigator.language : 'fr';
  return normalizeLang(navLang);
}

export const useSocialMediaNotify = () => {
  const { language } = useLanguage();

  const triggerSocialNotification = async (data: SocialNotifyData) => {
    const routeLanguage = resolveClientLanguage(language);
    const ctxNormalized = normalizeLang(language);

    try {
      console.log('[SocialNotify] pathname:', typeof window !== 'undefined' ? window.location.pathname : '(no-window)');
      console.log('[SocialNotify] html lang:', typeof document !== 'undefined' ? document.documentElement.getAttribute('lang') : '(no-doc)');
      console.log('[SocialNotify] context language:', language, '→', ctxNormalized);
      console.log('[SocialNotify] resolved routeLanguage:', routeLanguage);

      // On envoie la langue résolue en body + headers (double garde-fou)
      const { data: result, error } = await supabase.functions.invoke('social-media-notify', {
        body: {
          serverId: data.serverId,
          userLanguage: routeLanguage,
        },
        headers: {
          'x-rs-language': routeLanguage,
          'x-rs-route-lang': routeLanguage,
        },
      });

      if (error) {
        console.error('⚠️ Social media notification error:', error);
        throw error;
      }

      console.log('✅ Social media notifications triggered:', result);
      return result;
    } catch (err) {
      console.error('⚠️ Failed to trigger social media notifications:', err);
      throw err;
    }
  };

  return { triggerSocialNotification };
};
